// Sales Activities list — FE-SCRM-024 (+ FE-SCRM-059 rich body).
//
// A flat list (not a kanban) so producers can scan a long backlog
// quickly. Each row has inline complete/revert/cancel/delete actions.
// FE-SCRM-059: each row's primary content (actor badge + type icon +
// verb + subject) lives in `<ActivityRowRichBody>`. System-generated
// activities visually distinguish from manually-logged ones.

import { ActivityRowRichBody } from "./ActivityRowRichBody";
import type { ActivityType, SalesActivity } from "./types";
import { useActorResolver } from "./useActorResolver";


export function ActivityListTable({
  rows,
  on_complete,
  on_revert,
  on_cancel,
  on_delete,
  is_pending_id,
  on_quick_add,
}: {
  rows: SalesActivity[];
  on_complete: (a: SalesActivity) => void;
  on_revert: (a: SalesActivity) => void;
  on_cancel: (a: SalesActivity) => void;
  on_delete: (a: SalesActivity) => void;
  is_pending_id: string | null;
  /** FE-SCRM-038 — empty-state CTA target. When the list is empty,
   * we surface a "Schedule a discovery call" button that opens the
   * composer pre-filled with the ``discovery_call`` type. Optional
   * so embedded rails (Prospect / Opportunity detail) without a
   * composer host can drop the CTA. */
  on_quick_add?: (type: ActivityType) => void;
}) {
  if (rows.length === 0) {
    return (
      <div
        data-testid="activities-empty"
        className={[
          "rounded border border-dashed border-border-subtle p-6",
          "text-sm text-text-tertiary text-center",
          "flex flex-col items-center gap-3",
        ].join(" ")}
      >
        <span>
          No Sales Activities due this week.
        </span>
        {on_quick_add ? (
          <button
            type="button"
            data-testid="activities-empty-schedule-cta"
            onClick={() => on_quick_add("discovery_call")}
            className={[
              "inline-flex items-center justify-center rounded-md",
              "bg-teal px-4 py-2 text-sm font-medium",
              "text-text-inverse hover:opacity-90",
            ].join(" ")}
          >
            Schedule a discovery call
          </button>
        ) : null}
      </div>
    );
  }
  // FE-SCRM-059 — the actor resolver mounts a TanStack query against
  // the team-members endpoint. Gating it on a non-empty rows array
  // means empty rails (and the tests that don't supply rows) don't
  // need a fetchTeamMembers mock; only consumers that actually
  // render rows pay the resolver cost.
  return (
    <ResolverScopedList
      rows={rows}
      is_pending_id={is_pending_id}
      on_complete={on_complete}
      on_revert={on_revert}
      on_cancel={on_cancel}
      on_delete={on_delete}
    />
  );
}


function ResolverScopedList({
  rows, is_pending_id, on_complete, on_revert, on_cancel, on_delete,
}: {
  rows: SalesActivity[];
  on_complete: (a: SalesActivity) => void;
  on_revert: (a: SalesActivity) => void;
  on_cancel: (a: SalesActivity) => void;
  on_delete: (a: SalesActivity) => void;
  is_pending_id: string | null;
}) {
  const resolveActor = useActorResolver();
  return (
    <ul
      data-testid="activities-list"
      className="divide-y divide-border-subtle border border-border-subtle rounded"
    >
      {rows.map((row) => (
        <ActivityRow
          key={row.activity_id}
          row={row}
          pending={is_pending_id === row.activity_id}
          resolveActor={resolveActor}
          on_complete={on_complete}
          on_revert={on_revert}
          on_cancel={on_cancel}
          on_delete={on_delete}
        />
      ))}
    </ul>
  );
}


function ActivityRow({
  row, pending, resolveActor,
  on_complete, on_revert, on_cancel, on_delete,
}: {
  row: SalesActivity;
  pending: boolean;
  resolveActor: ReturnType<typeof useActorResolver>;
  on_complete: (a: SalesActivity) => void;
  on_revert: (a: SalesActivity) => void;
  on_cancel: (a: SalesActivity) => void;
  on_delete: (a: SalesActivity) => void;
}) {
  const is_open = row.status === "open";
  const is_completed = row.status === "completed";
  const due_label = row.due_at ? formatDueAt(row.due_at) : null;

  return (
    <li
      data-testid={`activity-row-${row.activity_id}`}
      className={[
        "flex items-center gap-3 px-3 py-2",
        is_completed ? "[&_[data-testid=activity-rich-body]_.font-medium]:line-through" : "",
      ].join(" ")}
    >
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <ActivityRowRichBody
            activity={row}
            resolveActorName={resolveActor}
          />
          <StatusBadge status={row.status} />
        </div>
        {due_label && (
          <div className="text-xs text-text-tertiary">{due_label}</div>
        )}
      </div>
      <div className="flex items-center gap-1">
        {is_open && (
          <button
            type="button"
            disabled={pending}
            data-testid={`activity-complete-${row.activity_id}`}
            onClick={() => on_complete(row)}
            className="text-xs px-2 py-1 rounded bg-teal text-white disabled:opacity-50"
          >
            Complete
          </button>
        )}
        {is_completed && (
          <button
            type="button"
            disabled={pending}
            data-testid={`activity-revert-${row.activity_id}`}
            onClick={() => on_revert(row)}
            className="text-xs px-2 py-1 rounded text-text-secondary hover:text-text-primary disabled:opacity-50"
          >
            Revert
          </button>
        )}
        {is_open && (
          <button
            type="button"
            disabled={pending}
            data-testid={`activity-cancel-${row.activity_id}`}
            onClick={() => on_cancel(row)}
            className="text-xs px-2 py-1 rounded text-text-secondary hover:text-text-primary disabled:opacity-50"
          >
            Cancel
          </button>
        )}
        <button
          type="button"
          disabled={pending}
          data-testid={`activity-delete-${row.activity_id}`}
          onClick={() => on_delete(row)}
          className="text-xs px-2 py-1 rounded text-text-tertiary hover:text-error disabled:opacity-50"
          aria-label="Delete activity"
        >
          ✕
        </button>
      </div>
    </li>
  );
}


function StatusBadge({ status }: { status: SalesActivity["status"] }) {
  const meta: Record<SalesActivity["status"], { label: string; cls: string }> = {
    open: { label: "Open", cls: "bg-teal/10 text-teal" },
    completed: { label: "Completed", cls: "bg-success/10 text-success" },
    cancelled: { label: "Cancelled", cls: "bg-recessed text-text-tertiary" },
  };
  const m = meta[status];
  return (
    <span
      data-testid={`activity-status-${status}`}
      className={[
        "text-[10px] uppercase tracking-wide rounded px-1.5 py-0.5",
        m.cls,
      ].join(" ")}
    >
      {m.label}
    </span>
  );
}


function formatDueAt(iso: string): string {
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return "";
  const today = new Date();
  const sameDay = (
    dt.getFullYear() === today.getFullYear()
    && dt.getMonth() === today.getMonth()
    && dt.getDate() === today.getDate()
  );
  const time = dt.toLocaleTimeString(undefined, {
    hour: "numeric", minute: "2-digit",
  });
  if (sameDay) return `Today, ${time}`;
  const date = dt.toLocaleDateString(undefined, {
    month: "short", day: "numeric",
    year: dt.getFullYear() === today.getFullYear() ? undefined : "numeric",
  });
  return `${date}, ${time}`;
}
